import { doc, getDoc } from 'firebase/firestore';

// How often we check Firestore for a new cron run (at most once per 15 min).
const META_CHECK_INTERVAL = 15 * 60 * 1000;

// localStorage keys
const META_CHECK_TS_KEY  = 'cacheMetaTs';    // timestamp of last Firestore meta check
const CACHE_VERSION_KEY  = 'cacheVersion';   // lastCronRun millis we last saw
const STATS_TOTALS_KEY   = 'statsTotals';    // { totalMinted, uniqueMinted, totalOwners }

// All localStorage cache keys that should be cleared when the cron runs.
const ALL_CACHE_KEYS = [
  'mythicData',
  'leaderboardData',
  'statsData',
  STATS_TOTALS_KEY,
  'coingecko_rates',
];

/**
 * Called once on app mount. Reads meta/version from Firestore (at most every
 * 15 min), clears all localStorage caches when the cron has a newer timestamp,
 * and caches the three stat totals so Stats.js can show numbers instantly
 * without an extra Firestore round-trip.
 */
export async function checkAndInvalidateCaches(db) {
  try {
    const lastCheck = Number(localStorage.getItem(META_CHECK_TS_KEY) || 0);
    if (Date.now() - lastCheck < META_CHECK_INTERVAL) return; // checked recently, skip

    const metaSnap = await getDoc(doc(db, 'meta', 'version'));
    const metaData = metaSnap.data() ?? {};
    const lastCronRun = metaData.lastCronRun?.toMillis?.() ?? 0;
    const knownVersion = Number(localStorage.getItem(CACHE_VERSION_KEY) || 0);

    if (lastCronRun > knownVersion) {
      ALL_CACHE_KEYS.forEach(k => localStorage.removeItem(k));
      localStorage.setItem(CACHE_VERSION_KEY, String(lastCronRun));
      console.log(`Cache invalidated — new cron run detected (${new Date(lastCronRun).toISOString()}).`);
    }

    // Cache the three stat totals so Stats.js can render numbers instantly
    // on the next visit without waiting for a stats/latest Firestore read.
    const totals = metaData.statsTotals ?? null;
    if (totals) {
      localStorage.setItem(STATS_TOTALS_KEY, JSON.stringify(totals));
    }

    localStorage.setItem(META_CHECK_TS_KEY, String(Date.now()));
  } catch (err) {
    // Non-fatal — if the meta check fails, cached data is still served normally.
    console.warn('cacheManager: failed to check meta/version:', err.message);
  }
}
